import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card, Flex, Grid, Typography } from 'antd'
import { GiftOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useCashPlans } from '@/features/cashdesk/cashdeskPlans'
import { DayRangeBar } from '@/features/profile/DayRangeBar'
import { OperationDetail } from '@/features/profile/OperationDetail'
import { OperationsList } from '@/features/profile/OperationsList'
import { GRANT_FILTER, type AgentOperation } from '@/features/profile/profileApi'
import { quickRange, tenantRangeWindow, type DayRange } from '@/shared/lib/tenantDay'
import { useAgentOperations, useMyAccount } from '@/features/profile/useMyAccount'
import { grantPlanLabel } from '@/shared/lib/planName'
import { EmptyState } from '@/shared/ui/EmptyState'

/** Besh ustunli jadval — undan kengrog'ida qatorlar bo'shab qoladi */
const PAGE_MAX_WIDTH = 1040

/**
 * «Berilgan paketlar» — egasi talabi, 2026-08-20.
 *
 * > «Bergan paketlarini ham ko'rish kerak — kimga qachon balansni
 * > paketga almashtirgan, shuni alohida menyu qilib chiqarib qo'yish
 * > kerak.»
 *
 * <h2>⚠️ PAKET NOMI IKKI MANBADAN, KATALOG BIRINCHI</h2>
 * Qator endi `planId` va `planName` beradi (server, 2026-08-20), lekin
 * `planName` KANONIK — bitta tilda. Ekranga esa foydalanuvchi tilidagi
 * nom kerak va u `/api/catalog/plans` dagi `nameI18n` da bor. Shuning
 * uchun tartib: `planId` -> katalog -> tarjima; topilmasa `planName`;
 * u ham yo'q bo'lsa ro'yxatda CHIZIQCHA, batafsil ko'rinishda esa
 * `planId` sababi bilan (`grantPlanLabel` javadoc'i).
 *
 * ⚠️ Katalog SAHIFADA olinadi, `OperationsList` ichida emas: u
 * `features/profile` da, katalog esa `features/cashdesk` da va
 * `features/*` bir-birini import qilmaydi (07-FRONTEND §6). Sahifa —
 * feature'larni yig'adigan yagona qatlam.
 *
 * ⚠️ Ikkinchi so'rov (`/api/catalog/plans`) AVTOYANGILANMAYDI: 5
 * daqiqalik kesh (`useCashPlans`) va kassa ekrani bilan AYNI kalit,
 * ya'ni kassadan kelgan sherik uchun tarmoqqa umuman chiqmaydi.
 *
 * <h2>⚠️ IKKINCHI ENDPOINT OCHILMADI</h2>
 * So'rov o'sha-o'sha (`kind=GRANT`), ajratilgani — EKRAN. Ikkinchi
 * manba ikkinchi kesh va ikkinchi bo'sh holat degani bo'lardi.
 *
 * <h2>⚠️ SUKUT DAVR — SHU OY</h2>
 * «Mening amallarim» da «bugun», chunki u savol kunlik («bugun nima
 * qildim»). Paket berish esa siyrak hodisa va bir kunlik oyna ro'yxatni
 * ko'pincha BO'SH ko'rsatardi — bo'shligi «hech kimga bermadim» bo'lib
 * o'qilardi.
 */
export function GrantedPlansPage() {
  const { t, i18n } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false
  const account = useMyAccount()
  const plans = useCashPlans()
  const language = i18n.language

  const planLabel = useCallback(
    (row: AgentOperation) => grantPlanLabel(row, plans.byId, language),
    [plans.byId, language],
  )

  const [range, setRange] = useState<DayRange>(() => quickRange('month'))
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<AgentOperation | null>(null)
  const window = useMemo(() => tenantRangeWindow(range), [range])

  // Davr o'zgarganda sahifa NOLGA va ochiq oyna yopiladi —
  // `MyOperationsPage` bilan ayni sabab.
  useEffect(() => {
    setPage(0)
    setSelected(null)
  }, [range])

  const operations = useAgentOperations(account.agentId, window, GRANT_FILTER, page)
  const rows = operations.page?.rows ?? []

  return (
    <div style={{ maxWidth: isMobile ? undefined : PAGE_MAX_WIDTH, margin: '0 auto' }}>
      {/* Sarlavha faqat kompyuterda — telefonda uni kabinetning yuqori
          qatori ko'rsatadi (`MyOperationsPage` bilan bir xil qaror). */}
      {isMobile ? null : (
        <div style={{ marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {t('grants.title')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('grants.subtitle')}</Typography.Text>
        </div>
      )}

      {account.agentId === null ? (
        <EmptyState
          icon={<GiftOutlined />}
          title={t('profile.noNodeTitle')}
          description={t('profile.noNodeText')}
        />
      ) : (
        <Flex vertical gap={16}>
          <DayRangeBar range={range} onChange={setRange} />

          <Card size="small" title={t('grants.listTitle')}>
            <OperationsList
              state={operations}
              onLoadMore={() => setPage((p) => p + 1)}
              onSelect={setSelected}
              variant="grants"
              planLabel={planLabel}
            />
          </Card>
        </Flex>
      )}

      <OperationDetail
        row={selected}
        rows={rows}
        planLabel={planLabel}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}
